import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { InteriorSlide, SlideBanner } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { PdfImage } from '../PdfImage';
import { boehringerImages } from '../../data/boehringerSnak';
const STAGES = [{
  phase: 'Attract',
  label: 'Hook',
  line: 'Earn the tap.',
  crop: boehringerImages.emailFull,
  alt: 'Boehringer email inviting GPs to find their high-risk patients'
}, {
  phase: 'Engage',
  label: 'Snak',
  line: 'Earn the interaction.',
  crop: boehringerImages.quizScreen,
  alt: 'Snak quiz asking what share of T2D and CVD patients receive neither SGLT2 nor GLP-1'
}, {
  phase: 'Learn',
  label: 'Signals',
  line: 'Understand what HCPs think.',
  crop: boehringerImages.quizResults,
  alt: 'Answer distribution showing 53% correct and 47% incorrect'
}, {
  phase: 'Improve',
  label: 'Next campaign',
  line: 'Use the learning.',
  crop: boehringerImages.ctaScreen,
  alt: 'Snak menu offering KiAP material and the next online meeting'
}] as const;
export function SlideSnakToCampaign({}: SlideProps) {
  return <InteriorSlide surface banner={<SlideBanner data-id="element-369" data-mp="10qnvxuksla62">Every campaign makes the next one smarter.</SlideBanner>} data-id="element-368" data-mp="10pcsjvv9e4fh">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-370" data-mp="14qy0r3wulujc">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-371" data-mp="d138aydp1oqg">
          From Snak to campaign
        </p>
        <h2 className="max-w-4xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-372" data-mp="lsdbziifj18v">
          How each campaign drives{' '}
          <span className="text-[var(--brand)]" data-id="element-373" data-mp="3leaifgwveqqv">the knowledge for the next.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] flex min-h-0 flex-1 items-stretch gap-[clamp(0.4rem,1vw,1rem)]" data-id="element-374" data-mp="wn87x2co0o6k">
        {STAGES.map((stage, index) => <React.Fragment key={stage.phase} data-id="element-375" data-mp="rnlvgv4nvbyt" data-mpi={index}>
            <motion.figure initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2 + index * 0.12,
          duration: 0.5
        }} className="flex min-h-0 min-w-0 flex-1 flex-col items-center" data-id="element-376" data-mp="16cruahzl7066">
              <span className="flex-shrink-0 rounded-full bg-[var(--brand)] px-[clamp(0.6rem,1vw,0.95rem)] py-[clamp(0.2rem,0.4vw,0.35rem)] text-[clamp(0.55rem,0.8vw,0.72rem)] font-bold uppercase tracking-[0.18em] text-white" data-id="element-377" data-mp="dq71dd3gqg0i">
                {stage.phase}
              </span>
              <div className="mt-[clamp(0.5rem,1vw,0.9rem)] flex min-h-0 flex-1 items-center justify-center" data-id="element-378" data-mp="im5n0sxepjx8">
                <PdfImage crop={stage.crop} alt={stage.alt} className="max-h-full w-auto max-w-full rounded-[1.25rem] bg-white object-contain drop-shadow-[0_18px_40px_rgba(23,32,51,0.16)]" data-id="element-379" data-mp="1y6abl9d8r47g" />
              </div>
              <figcaption className="mt-[clamp(0.5rem,1vw,0.9rem)] flex-shrink-0 text-center" data-id="element-380" data-mp="26mj5l8x2yu72">
                <p className="text-[clamp(0.95rem,1.4vw,1.35rem)] font-extrabold leading-tight tracking-[-0.025em] text-[var(--fg)]" data-id="element-381" data-mp="1rpid3372ev7j">
                  {stage.label}
                </p>
                <p className="mt-0.5 text-[clamp(0.7rem,1vw,0.92rem)] text-[var(--muted)]" data-id="element-382" data-mp="2kdxnfcf94pcj">
                  {stage.line}
                </p>
              </figcaption>
            </motion.figure>

            {index < STAGES.length - 1 && <motion.span initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          x: [0, 5, 0]
        }} transition={{
          opacity: {
            delay: 0.3 + index * 0.12,
            duration: 0.4
          },
          x: {
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: index * 0.3
          }
        }} className="flex h-9 w-9 flex-shrink-0 items-center justify-center self-center rounded-full bg-[var(--brand)] text-white shadow-sm" aria-hidden="true" data-id="element-383" data-mp="2akyx9q43wqqt">
                <ArrowRightIcon className="h-4 w-4" data-id="element-384" data-mp="15hegv3sal7ts" />
              </motion.span>}
          </React.Fragment>)}
      </div>
    </InteriorSlide>;
}